import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  Bot,
  Brain,
  Cog,
  FileJson,
  Layout,
  Search,
  Server,
  Sparkles,
  Eye,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const agentIcons: Record<string, typeof Bot> = {
  orchestrator: Cog,
  oracle: Brain,
  planning: Sparkles,
  frontend: Layout,
  backend: Server,
  mocking: FileJson,
  explorer: Search,
  reviewer: Eye,
  search: Search,
  general_chat: Bot,
};

const modelPresetColors: Record<string, string> = {
  'claude-opus': 'bg-purple-500',
  'claude-sonnet': 'bg-blue-500',
  'claude-haiku': 'bg-green-500',
  'gpt-pro': 'bg-emerald-600',
  'gpt-standard': 'bg-emerald-500',
  'gpt-chat': 'bg-teal-500',
  'gpt-mini': 'bg-cyan-500',
  'gemini-pro': 'bg-red-500',
  'gemini-flash': 'bg-orange-500',
};

const modelPresetLabels: Record<string, string> = {
  'claude-opus': 'Opus',
  'claude-sonnet': 'Sonnet',
  'claude-haiku': 'Haiku',
  'gpt-pro': 'GPT Pro',
  'gpt-standard': 'GPT Std',
  'gpt-chat': 'GPT Chat',
  'gpt-mini': 'GPT Mini',
  'gemini-pro': 'Gemini Pro',
  'gemini-flash': 'Gemini Flash',
};

const costTierBorderColors: Record<string, string> = {
  high: 'border-red-400',
  medium: 'border-yellow-400',
  low: 'border-green-400',
};

export interface AgentNodeData {
  type: string;
  name: string;
  nameKo?: string;
  modelTier?: string;
  costTier?: 'high' | 'medium' | 'low';
  onClick?: () => void;
}

function AgentNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as AgentNodeData;
  const IconComponent = agentIcons[nodeData.type] || Bot;
  const modelColor = nodeData.modelTier ? modelPresetColors[nodeData.modelTier] || 'bg-gray-500' : 'bg-gray-400';
  const modelLabel = nodeData.modelTier ? modelPresetLabels[nodeData.modelTier] || nodeData.modelTier : '-';
  const borderColor = nodeData.costTier ? costTierBorderColors[nodeData.costTier] : 'border-gray-300';

  return (
    <div
      className={`px-4 py-3 bg-background border-2 ${borderColor} rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer min-w-[140px]`}
      onClick={nodeData.onClick}
    >
      {/* Top Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-2 h-2 !bg-primary"
      />

      {/* Content */}
      <div className="flex flex-col items-center gap-2">
        <div className="p-2 rounded-lg bg-primary/10">
          <IconComponent className="h-5 w-5 text-primary" />
        </div>
        <div className="text-center">
          <div className="text-sm font-medium leading-tight">
            {(nodeData.nameKo || nodeData.name).replace(' Agent', '')}
          </div>
          <div className="text-xs text-muted-foreground">{nodeData.type}</div>
        </div>
        {nodeData.modelTier && (
          <Badge className={`${modelColor} text-white text-[10px] px-1.5 py-0`}>
            {modelLabel}
          </Badge>
        )}
      </div>

      {/* Bottom Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2 h-2 !bg-primary"
      />
    </div>
  );
}

export const AgentNode = memo(AgentNodeComponent);
